import { useState } from 'react'
import { X } from 'lucide-react'
import type { WireArticleBlock, WireVideoArtifact, WireImageArtifact, WireCaptionsArtifact } from '../../api/types'
import { CmtWrapper } from '../common/cmt'
import type { CmtResult } from '../common/cmt/types'

type VideoBlock = Extract<WireArticleBlock, { type: 'video' }>

// ─── Option tables ────────────────────────────────────────────────────────────

const WIDTH_OPTIONS: { value: NonNullable<VideoBlock['width']>; label: string }[] = [
  { value: 'column', label: 'Column' },
  { value: 'wide',   label: 'Wide'   },
  { value: 'full',   label: 'Full'   },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
      {children}
    </p>
  )
}

function fmtSeconds(s: number): string {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${String(sec).padStart(2, '0')}`
}

// ─── Video artifact picker ────────────────────────────────────────────────────

function VideoPicker({
  artifacts,
  onSelect,
  onClose,
}: {
  artifacts: WireVideoArtifact[]
  onSelect: (a: WireVideoArtifact) => void
  onClose: () => void
}) {
  return (
    <div className="rounded border border-line bg-canvas">
      <div className="flex items-center justify-between border-b border-line px-2 py-1.5">
        <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Select video artifact
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
          aria-label="Close picker"
        >
          <X size={12} />
        </button>
      </div>
      {artifacts.length === 0 ? (
        <p className="px-2 py-3 text-[11px] text-fg-secondary">
          No video artifacts uploaded yet.
        </p>
      ) : (
        <div className="space-y-1 p-2">
          {artifacts.map(a => (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a)}
              className="flex w-full items-center gap-2 rounded border border-line bg-fill px-2 py-1.5 text-left transition-colors hover:border-accent-primary-solid"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] font-semibold text-fg">{a.filename}</p>
                {a.duration !== undefined && (
                  <p className="text-[10px] text-fg-secondary">{fmtSeconds(a.duration / 1000)}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Poster image picker ──────────────────────────────────────────────────────

function PosterPicker({
  artifacts,
  onSelect,
  onClose,
}: {
  artifacts: WireImageArtifact[]
  onSelect: (a: WireImageArtifact | null) => void
  onClose: () => void
}) {
  return (
    <div className="rounded border border-line bg-canvas">
      <div className="flex items-center justify-between border-b border-line px-2 py-1.5">
        <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Select poster image
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
          aria-label="Close picker"
        >
          <X size={12} />
        </button>
      </div>
      <div className="p-2">
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="mb-1.5 w-full rounded border border-line bg-fill px-2 py-1 text-[11px] text-fg-secondary transition-colors hover:bg-canvas"
        >
          None
        </button>
        {artifacts.length === 0 ? (
          <p className="px-1 py-1 text-[11px] text-fg-secondary">No image artifacts uploaded yet.</p>
        ) : (
          <div className="grid grid-cols-3 gap-1.5">
            {artifacts.map(a => (
              <button
                key={a.id}
                type="button"
                onClick={() => onSelect(a)}
                className="overflow-hidden rounded border border-line bg-fill transition-colors hover:border-accent-primary-solid"
                title={a.filename}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={a.presignedUrl}
                    alt={a.altText ?? ''}
                    className="h-full w-full object-cover"
                  />
                </div>
                <p className="truncate px-1 py-0.5 text-[9px] text-fg-secondary">{a.filename}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Captions artifact picker ─────────────────────────────────────────────────

function CaptionsPicker({
  artifacts,
  onSelect,
  onClose,
}: {
  artifacts: WireCaptionsArtifact[]
  onSelect: (a: WireCaptionsArtifact | null) => void
  onClose: () => void
}) {
  return (
    <div className="rounded border border-line bg-canvas">
      <div className="flex items-center justify-between border-b border-line px-2 py-1.5">
        <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Select captions artifact
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
          aria-label="Close picker"
        >
          <X size={12} />
        </button>
      </div>
      <div className="p-2">
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="mb-1.5 w-full rounded border border-line bg-fill px-2 py-1 text-[11px] text-fg-secondary transition-colors hover:bg-canvas"
        >
          None
        </button>
        {artifacts.length === 0 ? (
          <p className="px-1 py-1 text-[11px] text-fg-secondary">No captions artifacts uploaded yet.</p>
        ) : (
          <div className="space-y-1">
            {artifacts.map(a => (
              <button
                key={a.id}
                type="button"
                onClick={() => onSelect(a)}
                className="flex w-full items-center gap-2 rounded border border-line bg-fill px-2 py-1.5 text-left transition-colors hover:border-accent-primary-solid"
              >
                <p className="truncate text-[11px] font-semibold text-fg">{a.filename}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  block: VideoBlock
  onUpdate: (updated: VideoBlock) => void
  videoArtifacts: WireVideoArtifact[]
  imageArtifacts: WireImageArtifact[]
  captionsArtifacts: WireCaptionsArtifact[]
}

export function VideoBlockPanel({ block, onUpdate, videoArtifacts, imageArtifacts, captionsArtifacts }: Props) {
  const [videoPickerOpen, setVideoPickerOpen]     = useState(false)
  const [posterPickerOpen, setPosterPickerOpen]   = useState(false)
  const [captionsPickerOpen, setCaptionsPickerOpen] = useState(false)
  const [captionCmt, setCaptionCmt]             = useState<CmtResult>({
    classification:        block.captionClassification        ?? 'U',
    disseminationControls: block.captionDisseminationControls ?? [],
  })

  const width    = block.width ?? 'column'
  const autoplay = block.autoplay ?? false
  const loop     = block.loop    ?? false
  const muted    = block.muted   ?? false

  const linkedArtifact  = videoArtifacts.find(a => a.id === block.artifactId)
  const posterArtifact  = imageArtifacts.find(a => a.id === block.posterArtifactId)

  function patch(p: Partial<VideoBlock>) {
    onUpdate({ ...block, ...p })
  }

  function handleCaptionCmtChange(cmt: CmtResult) {
    setCaptionCmt(cmt)
    patch({
      captionClassification:        cmt.classification,
      captionDisseminationControls: cmt.disseminationControls.length ? cmt.disseminationControls : undefined,
    })
  }

  return (
    <div className="space-y-4">

      {/* Video slot */}
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <FieldLabel>Video</FieldLabel>
          <button
            type="button"
            onClick={() => { setVideoPickerOpen(o => !o); setPosterPickerOpen(false) }}
            className="rounded border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          >
            {block.artifactId ? 'Change' : 'Select'}
          </button>
        </div>
        {videoPickerOpen && (
          <div className="mb-2">
            <VideoPicker
              artifacts={videoArtifacts}
              onSelect={a => { patch({ artifactId: a.id }); setVideoPickerOpen(false) }}
              onClose={() => setVideoPickerOpen(false)}
            />
          </div>
        )}
        {linkedArtifact ? (
          <div className="rounded border border-line bg-fill px-2 py-1.5">
            <p className="truncate text-[11px] font-semibold text-fg">{linkedArtifact.filename}</p>
            {linkedArtifact.duration !== undefined && (
              <p className="text-[10px] text-fg-secondary">{fmtSeconds(linkedArtifact.duration / 1000)}</p>
            )}
          </div>
        ) : (
          <p className="rounded border border-dashed border-red-7 px-2 py-2 text-center text-[11px] italic text-red-11 dark:text-red-11">
            No video linked — select an artifact above
          </p>
        )}
      </div>

      {/* Poster image */}
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <FieldLabel>Poster Image</FieldLabel>
          <button
            type="button"
            onClick={() => { setPosterPickerOpen(o => !o); setVideoPickerOpen(false) }}
            className="rounded border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          >
            {block.posterArtifactId ? 'Change' : 'Set'}
          </button>
        </div>
        {posterPickerOpen && (
          <div className="mb-2">
            <PosterPicker
              artifacts={imageArtifacts}
              onSelect={a => { patch({ posterArtifactId: a?.id ?? undefined }); setPosterPickerOpen(false) }}
              onClose={() => setPosterPickerOpen(false)}
            />
          </div>
        )}
        {posterArtifact && (
          <div className="overflow-hidden rounded border border-line">
            <img
              src={posterArtifact.presignedUrl}
              alt={posterArtifact.altText ?? ''}
              className="h-16 w-full object-cover"
            />
          </div>
        )}
      </div>

      {/* Captions */}
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <FieldLabel>Captions</FieldLabel>
          <button
            type="button"
            onClick={() => { setCaptionsPickerOpen(o => !o); setVideoPickerOpen(false); setPosterPickerOpen(false) }}
            className="rounded border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          >
            {block.captionsArtifactId ? 'Change' : 'Set'}
          </button>
        </div>
        {captionsPickerOpen && (
          <div className="mb-2">
            <CaptionsPicker
              artifacts={captionsArtifacts}
              onSelect={a => { patch({ captionsArtifactId: a?.id ?? undefined }); setCaptionsPickerOpen(false) }}
              onClose={() => setCaptionsPickerOpen(false)}
            />
          </div>
        )}
        {block.captionsArtifactId && (
          <div className="rounded border border-line bg-fill px-2 py-1.5">
            <p className="truncate text-[11px] font-semibold text-fg">
              {captionsArtifacts.find(a => a.id === block.captionsArtifactId)?.filename ?? block.captionsArtifactId}
            </p>
          </div>
        )}
      </div>

      {/* Width */}
      <div>
        <FieldLabel>Width</FieldLabel>
        <div className="flex gap-1">
          {WIDTH_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => patch({ width: opt.value })}
              className={`rounded border px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                width === opt.value
                  ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
                  : 'border-line bg-canvas text-fg-secondary hover:bg-fill'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Playback options */}
      <div>
        <FieldLabel>Playback</FieldLabel>
        <div className="space-y-1.5">
          {(
            [
              { key: 'muted',    label: 'Muted'    },
              { key: 'loop',     label: 'Loop'     },
              { key: 'autoplay', label: 'Autoplay (requires muted)' },
            ] as const
          ).map(({ key, label }) => (
            <label
              key={key}
              className="flex cursor-pointer items-center gap-2 text-[11px] text-fg"
            >
              <input
                type="checkbox"
                checked={key === 'autoplay' ? autoplay : key === 'loop' ? loop : muted}
                onChange={e => {
                  const val = e.target.checked
                  if (key === 'autoplay') {
                    patch({ autoplay: val, muted: val ? true : muted })
                  } else {
                    patch({ [key]: val })
                  }
                }}
                className="accent-accent-primary-solid"
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Alt text */}
      <div>
        <FieldLabel>Alt Text</FieldLabel>
        <input
          type="text"
          value={block.altText ?? ''}
          onChange={e => patch({ altText: e.target.value || undefined })}
          placeholder="Describe the video for accessibility"
          className="w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
        />
      </div>

      {/* Caption */}
      <div>
        <FieldLabel>Caption</FieldLabel>
        <textarea
          value={block.caption ?? ''}
          onChange={e => patch({ caption: e.target.value || undefined })}
          rows={2}
          placeholder="Caption text…"
          className="w-full resize-none rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
        />
        {block.caption && (
          <div className="mt-1.5">
            <FieldLabel>Caption Classification</FieldLabel>
            <CmtWrapper value={captionCmt} onChange={handleCaptionCmtChange} compact />
          </div>
        )}
      </div>

      {/* Credit */}
      <div>
        <FieldLabel>Credit</FieldLabel>
        <input
          type="text"
          value={block.credit ?? ''}
          onChange={e => patch({ credit: e.target.value || undefined })}
          placeholder="Source or attribution"
          className="w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
        />
      </div>

    </div>
  )
}
